import { useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import Lenis from 'lenis';
import '@/App.css';
import { SiteProvider, useSite } from '@/context/SiteContext';
import { LANGS, ROUTES, pathFor, routeKeyFromPath, BRAND_THEMES, THEME_BY_ROUTE } from '@/i18n/routes';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import CookieBanner from '@/components/CookieBanner';
import CustomCursor from '@/components/CustomCursor';
import IrisIntro from '@/components/IrisIntro';
import ChatWidget from '@/components/ChatWidget';
import IrisAssembly from '@/components/IrisAssembly';
import Home from '@/pages/Home';
import Media from '@/pages/Media';
import Souvenir from '@/pages/Souvenir';
import Portfolio from '@/pages/Portfolio';
import About from '@/pages/About';
import Contact from '@/pages/Contact';
import Legal from '@/pages/Legal';
import AdminPage from '@/pages/Admin';
import Project from '@/pages/Project';

const PAGES = {
  home: Home,
  media: Media,
  souvenir: Souvenir,
  portfolio: Portfolio,
  about: About,
  contact: Contact,
  privacy: () => <Legal type="privacy" />,
  cookies: () => <Legal type="cookies" />,
  terms: () => <Legal type="terms" />,
};

const BRAND_INTROS = {
  media: { color: 'green', label: 'F&L MEDIA', hex: '#14DA64' },
  souvenir: { color: 'blue', label: 'F&L SOUVENIR', hex: '#0CBEDD' },
};

function BrandIntro({ routeKey }) {
  const prev = useRef(null);
  const [active, setActive] = useState(null);
  const [fade, setFade] = useState(false);

  useEffect(() => {
    const isBrand = routeKey === 'media' || routeKey === 'souvenir';
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prev.current !== null && isBrand && prev.current !== routeKey && !reduced) {
      setActive(routeKey);
      setFade(false);
      const t1 = setTimeout(() => setFade(true), 1650);
      const t2 = setTimeout(() => setActive(null), 2100);
      prev.current = routeKey;
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
      };
    }
    prev.current = routeKey;
  }, [routeKey]);

  if (!active) return null;
  const brand = BRAND_INTROS[active];
  return (
    <div
      data-testid="brand-intro"
      className={`fixed inset-0 z-[140] flex items-center justify-center bg-black transition-opacity duration-500 ${
        fade ? 'opacity-0' : 'opacity-100'
      }`}
    >
      <IrisAssembly color={brand.color} fast label={brand.label} labelColor={brand.hex} size={180} />
    </div>
  );
}

function Page({ children }) {
  return (
    <motion.main
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.main>
  );
}

function LangRedirect() {
  const pref = localStorage.getItem('fl_lang');
  return <Navigate to={`/${LANGS.includes(pref) ? pref : 'pt'}`} replace />;
}

function NotFound() {
  const { lang, t } = useSite();
  return (
    <div className="flex min-h-[70vh] flex-col items-center justify-center px-5 pt-24 text-center" data-testid="not-found">
      <h1 className="font-heading text-5xl font-black uppercase">{t.notFound.title}</h1>
      <p className="mt-4 text-zinc-400">{t.notFound.text}</p>
      <Link
        to={pathFor('home', lang)}
        data-testid="notfound-home"
        className="mt-8 border px-6 py-3 font-mono text-xs uppercase tracking-widest transition-colors brand-border hover:!bg-[var(--brand)] hover:text-black"
      >
        {t.notFound.cta}
      </Link>
    </div>
  );
}

function SmartFallback() {
  const location = useLocation();
  const rawLang = location.pathname.split('/')[1];
  if (!LANGS.includes(rawLang)) return <Navigate to={`/pt${location.pathname}`} replace />;
  return (
    <Page>
      <NotFound />
    </Page>
  );
}

function Shell() {
  const location = useLocation();
  const rawLang = location.pathname.split('/')[1];
  const valid = LANGS.includes(rawLang);
  const stored = localStorage.getItem('fl_lang');
  const lang = valid ? rawLang : LANGS.includes(stored) ? stored : 'pt';
  const routeKey = valid ? routeKeyFromPath(lang, location.pathname.slice(lang.length + 1)) : 'home';

  useEffect(() => {
    window.__lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const theme = BRAND_THEMES[THEME_BY_ROUTE[routeKey] || 'group'];
    document.documentElement.style.setProperty('--brand', theme);
  }, [routeKey]);

  return (
    <SiteProvider lang={lang} routeKey={routeKey}>
      <Navbar />
      <BrandIntro routeKey={routeKey} />
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<LangRedirect />} />
          {LANGS.flatMap((l) =>
            Object.keys(ROUTES).map((key) => {
              const Comp = PAGES[key];
              return (
                <Route
                  key={`${l}-${key}`}
                  path={pathFor(key, l)}
                  element={
                    <Page>
                      <Comp />
                    </Page>
                  }
                />
              );
            })
          )}
          {LANGS.map((l) => (
            <Route
              key={`${l}-project`}
              path={`/${l}/portfolio/:slug`}
              element={
                <Page>
                  <Project />
                </Page>
              }
            />
          ))}
          <Route path="*" element={<SmartFallback />} />
        </Routes>
      </AnimatePresence>
      <Footer />
      <CookieBanner />
      <ChatWidget />
    </SiteProvider>
  );
}

export default function App() {
  const [introDone, setIntroDone] = useState(false);
  const isAdminPath = window.location.pathname.startsWith('/admin');

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <BrowserRouter>
      <CustomCursor />
      {!isAdminPath && <IrisIntro onDone={() => setIntroDone(true)} />}
      {(introDone || isAdminPath) && <RootSwitcher />}
    </BrowserRouter>
  );
}

function RootSwitcher() {
  const location = useLocation();
  if (location.pathname.startsWith('/admin')) return <AdminPage />;
  return <Shell />;
}
